import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginWithEmail } from '../auth/login';
import { signInWithGoogle } from '../auth/google';
import { useAuth } from '../auth/hooks';
import { HybridIcon } from '../components/HybridIcon';
import './Auth.css';
import { reloadUser } from '../auth/emailVerification';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/overview');
    }
  }, [isAuthenticated, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      alert('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const result = await loginWithEmail({ email, password });
      if (result.success) {
        // Reload user to get latest verification status
        await reloadUser();
        // The ProtectedRoute will check email verification and redirect if needed
        navigate('/overview');
      } else {
        alert(result.error || 'Login failed');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        // If user needs to set up organization, redirect to setup page
        if (result.needsSetup) {
          navigate('/setup-organization');
        } else {
          navigate('/overview');
        }
      } else {
        alert(result.error || 'Login failed');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <Link to="/" className="auth-back-button">
          <HybridIcon name="arrow-back" size={24} color="#666" />
          <span>Back to Home</span>
        </Link>
        <div className="auth-header">
          <HybridIcon name="car" size={64} color="#f4511e" />
          <h1 className="auth-title">Path Finder</h1>
          <p className="auth-subtitle">Fleet Management</p>
        </div>

        <form className="auth-form" onSubmit={handleEmailLogin}>
          <div className="auth-input-group">
            <HybridIcon name="mail" size={20} color="#666" className="auth-input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <div className="auth-input-group">
            <HybridIcon name="lock-closed" size={20} color="#666" className="auth-input-icon" />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            className="auth-button auth-button-primary"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-small" />
            ) : (
              'Sign In'
            )}
          </button>

          <div className="auth-divider">
            <div className="auth-divider-line" />
            <span className="auth-divider-text">OR</span>
            <div className="auth-divider-line" />
          </div>

          <button
            type="button"
            className="auth-button auth-button-google"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <HybridIcon name="logo-google" size={20} color="#fff" />
            <span>Continue with Google</span>
          </button>

          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-link auth-link-forgot">
              Forgot Password?
            </Link>
          </div>

          <div className="auth-footer">
            <span>Don't have an account? </span>
            <Link to="/signup" className="auth-link">Sign Up</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

